import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink, mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Facebook from "@insite/mobius/Icons/Facebook";
import Linkedin from "@insite/mobius/Icons/Linkedin";
import Twitter from "@insite/mobius/Icons/Twitter";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    name = "name",
    designation = "designation",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.name]: string;
        [fields.designation]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const link = getLink(state, ownProps.fields.imageLink);

    return { pageUrl: link?.url };
};

export interface TeamLinkStyles {
    wrapper?: InjectableCss;
    image?: LazyImageProps;
    imageLink?: LinkPresentationProps;
    name?: TypographyProps;
    designation?: TypographyProps;
    infoWrapper?: InjectableCss;
    ImgWrapper?: InjectableCss;
    ZoneOne?: InjectableCss;
    ZoneTwo?: InjectableCss;
    ZoneThree?: InjectableCss;
}

export const teamlinkStyles: TeamLinkStyles = {
    image: {
        css: css`
            width: 100%;
            height: auto;
            border-radius: 50%;
            & :hover {
                box-shadow: rgb(0 0 0 / 16%) 0px 3px 20px;
            }
        `,
    },
    ImgWrapper: {
        css: css`
            position: relative;
        `,
    },
    ZoneOne: {
        css: css`
            position: absolute;
            bottom: 3%;
            right: 15%;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            @media (max-width: 1199px) and (min-width: 992px) {
                top: 83%;
                right: 17%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        top: 86%;
                        right: 21%;
                    `,
                    css`
                        top: 83%;
                        right: 17%;
                    `,
                ])}
        `,
    },
    ZoneTwo: {
        css: css`
            position: absolute;
            bottom: 17%;
            right: 1%;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            @media (max-width: 1199px) and (min-width: 992px) {
                top: 73%;
                right: 5%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        top: 78%;
                        right: 10%;
                    `,
                    css`
                        top: 75%;
                        right: 7%;
                    `,
                    null,
                    null,
                    css`
                        bottom: 15%;
                    `,
                ])}
        `,
    },
    ZoneThree: {
        css: css`
            position: absolute;
            top: 48%;
            right: -6%;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            @media (max-width: 1199px) and (min-width: 992px) {
                top: 60%;
                right: -2%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        top: 67.5%;
                        right: 2%;
                    `,
                    css`
                        top: 65%;
                        right: 0%;
                    `,
                    null,
                    null,
                    css`
                        top: 55%;
                    `,
                ])}
        `,
    },
    name: {
        css: css`
            font-size: ${TYPOGRAPHY.size.large};
        `,
    },
    designation: {
        css: css`
            flex-grow: 1;
            margin-bottom: 10px;
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.bold};
            color: ${COLORS.primary.light};
            text-transform: uppercase;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 5px 22px;
            text-align: center;
        `,
    },
};

const styles = teamlinkStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const TeamLink: React.FunctionComponent<Props> = ({ id, fields, pageUrl }: Props) => {
    const image = <LazyImage src={fields.imageUrl} altText={fields.altText} {...styles.image} />;
    const TeamWrapperStyles = {
        css: css`
            width: 18%;
            height: auto;
            margin-right: 23px;
            margin-bottom: 45px;
            & :nth-child(5n) {
                margin-right: 0;
            }
            @media (max-width: 1199px) and (min-width: 992px) {
                width: 29%;
                & :nth-child(3n) {
                    margin-right: 0;
                }
                & :nth-child(5n) {
                    margin-right: 23px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        margin-right: 0;
                    `,
                    css`
                        width: 45%;
                        & :nth-child(even) {
                            margin-right: 0;
                        }
                        & :nth-child(5n) {
                            margin-right: 23px;
                        }
                    `,
                    css`
                        width: 29% !important;
                    `,
                    null,
                    css`
                        margin-right: 30px;
                        width: 252px;
                    `,
                ])}
        `,
    };
    return (
        <StyledWrapper {...TeamWrapperStyles}>
            <StyledWrapper {...styles.ImgWrapper}>
                {pageUrl ? (
                    <Link href={pageUrl} {...styles.imageLink}>
                        {image}
                    </Link>
                ) : (
                    image
                )}
                <StyledWrapper {...styles.ZoneOne}>
                    <Zone contentId={id} zoneName="contentOne" />
                </StyledWrapper>
                <StyledWrapper {...styles.ZoneTwo}>
                    <Zone contentId={id} zoneName="contentTwo" />
                </StyledWrapper>
                <StyledWrapper {...styles.ZoneThree}>
                    <Zone contentId={id} zoneName="contentThree" />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.infoWrapper}>
                <Typography {...styles.name}>{translate(fields.name)}</Typography>
                <Typography {...styles.designation}>{translate(fields.designation)}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const teamlink: WidgetModule = {
    component: TeamLink,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.imageUrl,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.altText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.imageLink,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Translatable",
            },
            {
                name: fields.name,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.designation,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default teamlink;
